import PptxGenJS from "pptxgenjs";
import type { DeckSpec, SlideSpec } from "./claude";

const HEX = (c: string) => c.replace("#", "").toUpperCase();

export interface DeckImages {
  /** índice do slide → dataURL da imagem (ou null) */
  [slideIdx: number]: string | null;
}

/** Constrói um PPTX a partir do deck + mapa de imagens já geradas. */
export type DeckAspect = "16:9" | "4:5" | "1:1" | "9:16";

export interface BuildOpts {
  aspect?: DeckAspect;
}

const ASPECT_DIMS: Record<DeckAspect, { w: number; h: number; layoutName: string }> = {
  "16:9": { w: 13.33, h: 7.5,   layoutName: "ASPECT_16_9" },
  "4:5":  { w: 8,     h: 10,    layoutName: "ASPECT_4_5"  },  // ~ 1080x1350 Instagram feed
  "1:1":  { w: 10,    h: 10,    layoutName: "ASPECT_1_1"  },  // square
  "9:16": { w: 7.5,   h: 13.33, layoutName: "ASPECT_9_16" }   // story/reel
};

export async function buildPptx(deck: DeckSpec, images: DeckImages, filename: string, opts: BuildOpts = {}) {
  const pptx = new PptxGenJS();
  const aspect = opts.aspect ?? "16:9";
  const dims = ASPECT_DIMS[aspect];
  pptx.defineLayout({ name: dims.layoutName, width: dims.w, height: dims.h });
  pptx.layout = dims.layoutName;
  pptx.author = "Agência VPC";
  pptx.title  = deck.title;

  const PRIMARY   = HEX(deck.brandPrimary   ?? "#121214");
  const SECONDARY = HEX(deck.brandSecondary ?? "#A8863C");
  const ACCENT    = HEX(deck.brandAccent    ?? "#F2EEE6");
  const FG_ON_PRIMARY = ACCENT;
  const FG_ON_BONE    = PRIMARY;

  // master adaptado ao aspecto
  pptx.defineSlideMaster({
    title: "MASTER_BONE",
    background: { color: ACCENT },
    objects: [
      { rect: { x: 0.3, y: 0.2, w: dims.w - 0.6, h: 0.02, fill: { color: PRIMARY } } },
      { text: {
          text: "AGÊNCIA VPC",
          options: { x: 0.3, y: 0.05, w: 3, h: 0.18, fontFace: "Courier New",
                     fontSize: 7, color: PRIMARY, bold: true }
      } },
      { text: {
          text: deck.title.slice(0, 60),
          options: { x: dims.w - 4.3, y: 0.05, w: 4, h: 0.18, align: "right",
                     fontFace: "Courier New", fontSize: 7, color: PRIMARY }
      } }
    ]
  });

  deck.slides.forEach((spec, idx) => {
    const slide = pptx.addSlide({ masterName: "MASTER_BONE" });
    renderSlide(pptx, slide, spec, images[idx] ?? null,
      { PRIMARY, SECONDARY, ACCENT, FG_ON_PRIMARY, FG_ON_BONE }, dims, aspect);
  });

  await pptx.writeFile({ fileName: filename });
}

function renderSlide(_pptx: PptxGenJS, slide: any, spec: SlideSpec, imageDataUrl: string | null,
  colors: { PRIMARY: string; SECONDARY: string; ACCENT: string; FG_ON_PRIMARY: string; FG_ON_BONE: string },
  dims: { w: number; h: number },
  aspect: DeckAspect) {

  const { PRIMARY, SECONDARY, ACCENT, FG_ON_PRIMARY, FG_ON_BONE } = colors;
  const layout = spec.layout ?? "bullets";

  // Em formatos verticais (4:5, 9:16) — pular layouts side-by-side, virar stacked
  const isVertical = aspect === "4:5" || aspect === "9:16";
  const W = dims.w;
  const H = dims.h;

  // Helper: para vertical, mudar "image-left/right" pra "image-top" (stacked)
  const effLayout = isVertical && (layout === "image-left" || layout === "image-right")
    ? "image-top"
    : layout;

  // Escala de fontes adaptada ao aspecto
  const FS = {
    coverTitle:  isVertical ? (aspect === "9:16" ? 56 : 48) : 72,
    coverSub:    isVertical ? 18 : 24,
    section:     isVertical ? (aspect === "9:16" ? 64 : 56) : 96,
    bulletsTitle: isVertical ? 28 : 40,
    bulletsBody: isVertical ? 16 : 22,
    metricVal:   isVertical ? 140 : 200,
    metricCap:   isVertical ? 20 : 24,
    quoteTxt:    isVertical ? 28 : 44,
    quoteAuth:   isVertical ? 14 : 18,
    closingTit:  isVertical ? 40 : 56,
    closingBul:  isVertical ? 18 : 24
  };
  const PAD = 0.5;

  switch (effLayout) {
    case "cover": {
      slide.background = { color: PRIMARY };
      if (imageDataUrl) {
        slide.addImage({ data: imageDataUrl, x: 0, y: 0, w: W, h: H, sizing: { type: "cover", w: W, h: H } });
        slide.addShape("rect", { x: 0, y: 0, w: W, h: H, fill: { color: PRIMARY, transparency: 35 }, line: { color: PRIMARY, width: 0 } });
      }
      const titleY = isVertical ? H * 0.4 : H * 0.33;
      slide.addText(spec.title, {
        x: PAD, y: titleY, w: W - PAD * 2, h: H * 0.25,
        fontSize: FS.coverTitle, fontFace: "Impact", bold: true, color: FG_ON_PRIMARY,
        align: isVertical ? "center" : "left",
        valign: "middle"
      });
      if (spec.subtitle) {
        slide.addText(spec.subtitle, {
          x: PAD, y: titleY + H * 0.25 + 0.1, w: W - PAD * 2, h: 0.8,
          fontSize: FS.coverSub, fontFace: "Georgia", italic: true, color: FG_ON_PRIMARY,
          align: isVertical ? "center" : "left"
        });
      }
      slide.addShape("rect", {
        x: isVertical ? W/2 - 0.3 : PAD, y: H - 0.8, w: 0.6, h: 0.08,
        fill: { color: SECONDARY }, line: { color: SECONDARY, width: 0 }
      });
      break;
    }

    case "section": {
      slide.background = { color: PRIMARY };
      slide.addText(spec.title.toUpperCase(), {
        x: PAD, y: H * 0.3, w: W - PAD * 2, h: H * 0.4,
        fontSize: FS.section, fontFace: "Impact", bold: true, color: FG_ON_PRIMARY,
        align: "center", valign: "middle"
      });
      slide.addShape("rect", { x: W/2 - 0.5, y: H - 1, w: 1, h: 0.08, fill: { color: SECONDARY }, line: { color: SECONDARY, width: 0 } });
      break;
    }

    case "image-top": {
      // formato vertical com imagem em cima ocupando ~50%, texto embaixo
      const imgH = H * 0.5;
      if (imageDataUrl) {
        slide.addImage({ data: imageDataUrl, x: 0, y: 0, w: W, h: imgH, sizing: { type: "cover", w: W, h: imgH } });
      }
      slide.addShape("rect", { x: PAD, y: imgH + 0.3, w: 0.5, h: 0.06, fill: { color: SECONDARY }, line: { color: SECONDARY, width: 0 } });
      slide.addText(spec.title, {
        x: PAD, y: imgH + 0.5, w: W - PAD * 2, h: 1,
        fontSize: FS.bulletsTitle, fontFace: "Impact", bold: true, color: FG_ON_BONE
      });
      const textY = imgH + 1.5;
      if (spec.body) {
        slide.addText(spec.body, {
          x: PAD, y: textY, w: W - PAD * 2, h: H - textY - PAD,
          fontSize: FS.bulletsBody - 2, fontFace: "Georgia", color: FG_ON_BONE, valign: "top"
        });
      }
      if (spec.bullets?.length) {
        slide.addText(spec.bullets.map(b => ({ text: b, options: { bullet: { code: "25A0" }, color: FG_ON_BONE } })), {
          x: PAD, y: textY, w: W - PAD * 2, h: H - textY - PAD,
          fontSize: FS.bulletsBody, fontFace: "Calibri", color: FG_ON_BONE,
          paraSpaceAfter: 8
        });
      }
      break;
    }

    case "image-left":
    case "image-right": {
      const imgLeft = effLayout === "image-left";
      const imgW = W * 0.5;
      if (imageDataUrl) {
        slide.addImage({
          data: imageDataUrl,
          x: imgLeft ? 0 : W - imgW, y: 0, w: imgW, h: H,
          sizing: { type: "cover", w: imgW, h: H }
        });
      }
      const tx = imgLeft ? imgW + PAD : PAD;
      const tw = imgW - PAD * 2;
      slide.addText(spec.title, {
        x: tx, y: 1, w: tw, h: 1.5,
        fontSize: FS.bulletsTitle - 4, fontFace: "Impact", bold: true, color: FG_ON_BONE
      });
      slide.addShape("rect", { x: tx, y: 0.9, w: 0.4, h: 0.06, fill: { color: SECONDARY }, line: { color: SECONDARY, width: 0 } });
      if (spec.body) {
        slide.addText(spec.body, {
          x: tx, y: 2.7, w: tw, h: H - 3.5,
          fontSize: FS.bulletsBody - 4, fontFace: "Georgia", color: FG_ON_BONE, valign: "top"
        });
      }
      if (spec.bullets?.length) {
        slide.addText(spec.bullets.map(b => ({ text: b, options: { bullet: { code: "25A0" }, color: FG_ON_BONE } })), {
          x: tx, y: 2.7, w: tw, h: H - 3.5,
          fontSize: FS.bulletsBody - 2, fontFace: "Calibri", color: FG_ON_BONE,
          paraSpaceAfter: 6
        });
      }
      break;
    }

    case "metric": {
      slide.addText(spec.title.toUpperCase(), {
        x: PAD, y: 0.6, w: W - PAD * 2, h: 0.5,
        fontSize: 12, fontFace: "Courier New", color: FG_ON_BONE, bold: true,
        align: isVertical ? "center" : "left"
      });
      slide.addText(spec.metric?.value ?? "—", {
        x: PAD, y: H * 0.25, w: W - PAD * 2, h: H * 0.5,
        fontSize: FS.metricVal, fontFace: "Impact", bold: true, color: SECONDARY,
        align: "center", valign: "middle"
      });
      if (spec.metric?.caption) {
        slide.addText(spec.metric.caption, {
          x: PAD, y: H * 0.78, w: W - PAD * 2, h: 1,
          fontSize: FS.metricCap, fontFace: "Georgia", italic: true, color: FG_ON_BONE, align: "center"
        });
      }
      break;
    }

    case "quote": {
      slide.background = { color: PRIMARY };
      slide.addText(`"${spec.quote?.text ?? ""}"`, {
        x: PAD, y: H * 0.25, w: W - PAD * 2, h: H * 0.5,
        fontSize: FS.quoteTxt, fontFace: "Georgia", italic: true, color: FG_ON_PRIMARY,
        align: "center", valign: "middle"
      });
      slide.addText(`— ${spec.quote?.author ?? ""}`, {
        x: PAD, y: H - 1.2, w: W - PAD * 2, h: 0.6,
        fontSize: FS.quoteAuth, fontFace: "Courier New", color: SECONDARY, align: "center"
      });
      break;
    }

    case "closing": {
      slide.background = { color: SECONDARY };
      slide.addText(spec.title.toUpperCase(), {
        x: PAD, y: 0.8, w: W - PAD * 2, h: 1.5,
        fontSize: FS.closingTit, fontFace: "Impact", bold: true, color: PRIMARY
      });
      if (spec.bullets?.length) {
        slide.addText(spec.bullets.map((b, i) => ({
          text: `${String(i+1).padStart(2,"0")}.  ${b}`,
          options: { color: PRIMARY }
        })), {
          x: PAD, y: 2.5, w: W - PAD * 2, h: H - 4,
          fontSize: FS.closingBul, fontFace: "Georgia", color: PRIMARY,
          paraSpaceAfter: 10
        });
      }
      slide.addText("AGÊNCIA VPC · R3 GROUP LLC", {
        x: PAD, y: H - 0.4, w: W - PAD * 2, h: 0.3,
        fontSize: 9, fontFace: "Courier New", color: PRIMARY
      });
      break;
    }

    default: // "bullets"
      slide.addText(spec.title, {
        x: PAD, y: 0.7, w: W - PAD * 2, h: 1,
        fontSize: FS.bulletsTitle, fontFace: "Impact", bold: true, color: FG_ON_BONE
      });
      slide.addShape("rect", { x: PAD, y: 1.7, w: 0.5, h: 0.06, fill: { color: SECONDARY }, line: { color: SECONDARY, width: 0 } });
      if (spec.body) {
        slide.addText(spec.body, {
          x: PAD, y: 2.1, w: W - PAD * 2, h: H - 3,
          fontSize: FS.bulletsBody - 2, fontFace: "Georgia", color: FG_ON_BONE, valign: "top"
        });
      }
      if (spec.bullets?.length) {
        slide.addText(spec.bullets.map(b => ({ text: b, options: { bullet: { code: "25A0" }, color: FG_ON_BONE } })), {
          x: PAD, y: 2.1, w: W - PAD * 2, h: H - 3,
          fontSize: FS.bulletsBody, fontFace: "Calibri", color: FG_ON_BONE,
          paraSpaceAfter: 12
        });
      }
      if (imageDataUrl && !isVertical) {
        slide.addImage({ data: imageDataUrl, x: W - 4, y: 0.7, w: 3.5, h: 2.5 });
      } else if (imageDataUrl && isVertical) {
        slide.addImage({ data: imageDataUrl, x: PAD, y: H * 0.6, w: W - PAD * 2, h: H * 0.35, sizing: { type: "cover", w: W - PAD * 2, h: H * 0.35 } });
      }
  }
}
